// Draven Spinning Axe VFX — 4-phase animation.
//   1. THROW   — axe spins toward target; shadow indicator shows at catch spot
//   2. IMPACT  — brief flash on hit, damage fires; indicator still visible
//   3. RETURN  — axe arcs back on a high parabola peaking at the midpoint
//   4. CATCH   — starburst flash at caster

import type { Animation } from "../../engine/animation/AnimationQueue";
import { Easing } from "../../engine/animation/Easing";
import type { Point } from "../../engine/hex/HexLayout";

const THROW_MS  = 390;
const IMPACT_MS = 80;
const RETURN_MS = 530;
const CATCH_MS  = 140;
const TOTAL = THROW_MS + IMPACT_MS + RETURN_MS + CATCH_MS;

// ~1.3 full rotations during the throw — clearly spinning, not a blur
const SPIN_RATE = 0.022;
// Arc peak above the path midpoint; control point is placed at 2× this.
const PEAK_H    = 120;

export class DravenAxeVFX implements Animation {
  private elapsed = 0;
  private impactFired = false;
  private catchFired = false;

  constructor(
    private from: Point,
    private to: Point,
    private getCatchPos: () => Point,
    private onImpact?: () => void,
    private onCatch?: () => void,
  ) {}

  update(dt: number): void {
    this.elapsed = Math.min(this.elapsed + dt, TOTAL);
    if (!this.impactFired && this.elapsed >= THROW_MS) {
      this.impactFired = true;
      this.onImpact?.();
    }
    if (!this.catchFired && this.elapsed >= THROW_MS + IMPACT_MS + RETURN_MS) {
      this.catchFired = true;
      this.onCatch?.();
    }
  }

  draw(ctx: CanvasRenderingContext2D): void {
    const e = this.elapsed;
    const catchPos = this.getCatchPos();

    if (e < THROW_MS) {
      const p = Easing.easeOutQuad(e / THROW_MS);
      const x = this.from.x + (this.to.x - this.from.x) * p;
      const y = this.from.y + (this.to.y - this.from.y) * p;
      this.drawShadowIndicator(ctx, catchPos.x, catchPos.y, e, 1.0);
      this.drawAxe(ctx, x, y, e * SPIN_RATE);

    } else if (e < THROW_MS + IMPACT_MS) {
      const p = (e - THROW_MS) / IMPACT_MS;
      this.drawShadowIndicator(ctx, catchPos.x, catchPos.y, e, 1.0);
      this.drawImpact(ctx, this.to.x, this.to.y, p);

    } else if (e < THROW_MS + IMPACT_MS + RETURN_MS) {
      const rp = (e - THROW_MS - IMPACT_MS) / RETURN_MS;
      const indFade = rp < 0.65 ? 1 : 1 - (rp - 0.65) / 0.35;
      this.drawShadowIndicator(ctx, catchPos.x, catchPos.y, e, indFade);

      // Control point at horizontal midpoint, lifted 2×PEAK_H so the bezier
      // peaks exactly halfway through the return journey.
      const ctrlX = (this.to.x + catchPos.x) / 2;
      const ctrlY = (this.to.y + catchPos.y) / 2 - PEAK_H * 2;
      const p  = Easing.easeInOutQuad(rp);
      const bx = (1-p)*(1-p)*this.to.x  + 2*(1-p)*p*ctrlX + p*p*catchPos.x;
      const by = (1-p)*(1-p)*this.to.y  + 2*(1-p)*p*ctrlY + p*p*catchPos.y;
      this.drawAxe(ctx, bx, by, e * SPIN_RATE);

    } else {
      const p = (e - THROW_MS - IMPACT_MS - RETURN_MS) / CATCH_MS;
      this.drawCatch(ctx, catchPos.x, catchPos.y, p);
    }
  }

  // ─── Ground shadow indicator ───────────────────────────────────────────────

  private drawShadowIndicator(
    ctx: CanvasRenderingContext2D,
    x: number, y: number,
    elapsed: number,
    fadeAlpha: number,
  ): void {
    if (fadeAlpha <= 0) return;

    const pulse     = 0.65 + 0.35 * Math.sin(elapsed * 0.007);
    const spinAngle = elapsed * 0.0025;
    const RX = 33;   // wide ellipse reads as a circle lying on the ground
    const RY = 10;

    ctx.save();
    ctx.translate(x, y + 14); // shift down so the ellipse sits below the sprite

    // Soft shadow blob
    ctx.globalAlpha = fadeAlpha * 0.50 * pulse;
    ctx.beginPath();
    ctx.ellipse(0, 0, RX, RY, 0, 0, Math.PI * 2);
    ctx.fillStyle = "rgba(0,0,0,0.55)";
    ctx.fill();

    // 4 spinning dashed arcs traced along the ellipse perimeter
    ctx.globalAlpha = fadeAlpha * 0.90 * pulse;
    ctx.shadowColor = "#ff4400";
    ctx.shadowBlur  = 10;
    ctx.strokeStyle = "#ff6622";
    ctx.lineWidth   = 2;

    const arcFrac = 0.26;
    const steps   = 14;
    for (let seg = 0; seg < 4; seg++) {
      const baseA = (seg / 4) * Math.PI * 2 + spinAngle;
      const endA  = baseA + Math.PI * 2 * arcFrac;
      ctx.beginPath();
      for (let s = 0; s <= steps; s++) {
        const a  = baseA + (endA - baseA) * s / steps;
        const px = RX * Math.cos(a);
        const py = RY * Math.sin(a);
        if (s === 0) ctx.moveTo(px, py); else ctx.lineTo(px, py);
      }
      ctx.stroke();
    }

    ctx.restore();
  }

  // ─── Axe: double-ended glaive ──────────────────────────────────────────────

  private drawAxe(ctx: CanvasRenderingContext2D, x: number, y: number, rotation: number): void {
    ctx.save();
    ctx.translate(x, y);
    ctx.rotate(rotation);

    // Top blade
    this.drawBlade(ctx);

    // Bottom blade — vertical mirror
    ctx.save();
    ctx.scale(1, -1);
    this.drawBlade(ctx);
    ctx.restore();

    // Central ring hilt on top of both blades
    this.drawRing(ctx);

    ctx.restore();
  }

  // One blade, pointing upward (−Y). Call with scale(1,−1) for the bottom.
  private drawBlade(ctx: CanvasRenderingContext2D): void {
    // ── Dark obsidian body — cleaver silhouette with beard ──
    ctx.beginPath();
    ctx.moveTo(-5, -7);    // spine base (ring attachment)
    ctx.lineTo(-6, -18);   // spine mid
    ctx.lineTo(-4, -25);   // spine upper
    ctx.lineTo(0,  -29);   // blade tip
    ctx.lineTo(12, -20);   // upper cutting edge
    ctx.lineTo(14, -13);   // belly — widest point
    ctx.lineTo(10, -7);    // lower cutting edge / beard root
    ctx.lineTo(13, -2);    // beard tip (protrudes toward center)
    ctx.lineTo(7,   1);    // beard lower corner
    ctx.lineTo(3,  -4);    // beard back to ring
    ctx.closePath();
    ctx.fillStyle  = "#222530";
    ctx.shadowColor = "#000000";
    ctx.shadowBlur  = 5;
    ctx.fill();

    // ── Spine thorns — 2 small triangular protrusions ──
    for (const thornyY of [-13, -20]) {
      ctx.beginPath();
      ctx.moveTo(-5, thornyY + 3);   // thorn base lower
      ctx.lineTo(-13, thornyY - 1);  // thorn tip
      ctx.lineTo(-5, thornyY - 4);   // thorn base upper
      ctx.closePath();
      ctx.fillStyle  = "#2e3240";
      ctx.shadowBlur = 0;
      ctx.fill();
      ctx.strokeStyle = "#505870";
      ctx.lineWidth   = 0.8;
      ctx.stroke();
    }

    // ── Weathered steel cutting edge — bright strip ──
    ctx.beginPath();
    ctx.moveTo(0,  -29);
    ctx.lineTo(12, -20);
    ctx.lineTo(14, -13);
    ctx.lineTo(10, -7);
    ctx.strokeStyle = "#bcc8dc";
    ctx.lineWidth   = 2.2;
    ctx.shadowColor = "#dce8ff";
    ctx.shadowBlur  = 7;
    ctx.stroke();

    // Tip sparkle
    ctx.beginPath();
    ctx.arc(0, -29, 1.8, 0, Math.PI * 2);
    ctx.fillStyle = "#dce8f8";
    ctx.fill();

    // ── Gold filigree / runic line across the dark blade flat ──
    ctx.beginPath();
    ctx.moveTo(-2, -23);
    ctx.quadraticCurveTo(5, -16, 9, -9);
    ctx.strokeStyle = "#c09030";
    ctx.lineWidth   = 1;
    ctx.shadowColor = "#ffcc44";
    ctx.shadowBlur  = 5;
    ctx.stroke();

    // ── 3 glowing red / amber gems set into a dark channel ──
    const gems = [
      { x: 4, y: -21 },
      { x: 7, y: -15 },
      { x: 9, y:  -9 },
    ];
    for (const g of gems) {
      // Recessed dark metal channel
      ctx.shadowBlur = 0;
      ctx.beginPath();
      ctx.ellipse(g.x, g.y, 3.2, 1.9, -0.3, 0, Math.PI * 2);
      ctx.fillStyle = "#14121a";
      ctx.fill();
      // Gem body
      ctx.shadowColor = "#ff2200";
      ctx.shadowBlur  = 12;
      ctx.beginPath();
      ctx.ellipse(g.x, g.y, 2.3, 1.4, -0.3, 0, Math.PI * 2);
      ctx.fillStyle = "#cc3311";
      ctx.fill();
      // Gem inner bright
      ctx.shadowBlur = 0;
      ctx.beginPath();
      ctx.ellipse(g.x, g.y, 1.6, 0.9, -0.3, 0, Math.PI * 2);
      ctx.fillStyle = "#ee5533";
      ctx.fill();
      // Specular highlight
      ctx.beginPath();
      ctx.ellipse(g.x - 0.6, g.y - 0.45, 0.85, 0.5, -0.3, 0, Math.PI * 2);
      ctx.fillStyle = "#ff9977";
      ctx.fill();
    }
  }

  // Chakram-style center ring hilt with red cord wrap and 4 diagonal spikes.
  private drawRing(ctx: CanvasRenderingContext2D): void {
    const rR = 7.5; // ring radius

    // ── Red cord wrap fill (clipped to circle) ──
    ctx.save();
    ctx.beginPath();
    ctx.arc(0, 0, rR, 0, Math.PI * 2);
    ctx.clip();

    ctx.fillStyle = "#6a1818";
    ctx.beginPath();
    ctx.arc(0, 0, rR, 0, Math.PI * 2);
    ctx.fill();

    // Diagonal wrap lines suggesting leather cord
    ctx.strokeStyle = "#8a2828";
    ctx.lineWidth   = 1.2;
    for (let i = -4; i <= 4; i++) {
      ctx.beginPath();
      ctx.moveTo(-rR, i * 2.8);
      ctx.lineTo( rR, i * 2.8 - 4);
      ctx.stroke();
    }
    ctx.restore();

    // ── Gold outer ring border ──
    ctx.beginPath();
    ctx.arc(0, 0, rR, 0, Math.PI * 2);
    ctx.strokeStyle = "#c09030";
    ctx.lineWidth   = 1.8;
    ctx.shadowColor = "#ffcc44";
    ctx.shadowBlur  = 8;
    ctx.stroke();

    // Inner ring line
    ctx.beginPath();
    ctx.arc(0, 0, rR * 0.72, 0, Math.PI * 2);
    ctx.strokeStyle = "#906820";
    ctx.lineWidth   = 0.8;
    ctx.shadowBlur  = 0;
    ctx.stroke();

    // ── 4 diagonal outward spikes at 45°/135°/225°/315° ──
    const spikeLen  = 9;
    const spikeBase = 3.5;
    for (let i = 0; i < 4; i++) {
      const angle = Math.PI / 4 + (i / 4) * Math.PI * 2;
      const cos   = Math.cos(angle);
      const sin   = Math.sin(angle);
      // Perpendicular unit vector for the base width
      const px = -sin;
      const py =  cos;

      ctx.beginPath();
      ctx.moveTo(cos * rR + px * spikeBase, sin * rR + py * spikeBase); // base left
      ctx.lineTo(cos * (rR + spikeLen),     sin * (rR + spikeLen));     // tip
      ctx.lineTo(cos * rR - px * spikeBase, sin * rR - py * spikeBase); // base right
      ctx.closePath();
      ctx.fillStyle  = "#b08828";
      ctx.shadowColor = "#ffcc44";
      ctx.shadowBlur  = 8;
      ctx.fill();
      // Spike leading-edge highlight
      ctx.beginPath();
      ctx.moveTo(cos * rR + px * spikeBase, sin * rR + py * spikeBase);
      ctx.lineTo(cos * (rR + spikeLen),     sin * (rR + spikeLen));
      ctx.strokeStyle = "#e0c050";
      ctx.lineWidth   = 0.9;
      ctx.shadowBlur  = 0;
      ctx.stroke();
    }
  }

  // ─── Impact / Catch effects ────────────────────────────────────────────────

  private drawImpact(ctx: CanvasRenderingContext2D, x: number, y: number, t: number): void {
    const fade = 1 - t;
    ctx.save();
    ctx.shadowColor = "#ff4400";
    ctx.shadowBlur  = 24;

    ctx.globalAlpha = fade * 0.9;
    ctx.strokeStyle = "#ff6622";
    ctx.lineWidth   = 2.5;
    ctx.beginPath();
    ctx.arc(x, y, 14 + t * 18, 0, Math.PI * 2);
    ctx.stroke();

    ctx.globalAlpha = fade * 0.75;
    ctx.fillStyle   = "#ffffff";
    ctx.beginPath();
    ctx.arc(x, y, 5 * (1 - t), 0, Math.PI * 2);
    ctx.fill();

    ctx.restore();
  }

  private drawCatch(ctx: CanvasRenderingContext2D, x: number, y: number, t: number): void {
    const fade = 1 - t;
    ctx.save();
    ctx.shadowColor = "#ff4400";
    ctx.shadowBlur  = 26;

    // 8 starburst rays in red-gold
    ctx.globalAlpha = fade * 0.9;
    ctx.strokeStyle = "#ff6622";
    ctx.lineWidth   = 2;
    for (let i = 0; i < 8; i++) {
      const angle = (i / 8) * Math.PI * 2;
      const len   = 16 * (1 - t * 0.5);
      ctx.beginPath();
      ctx.moveTo(x + Math.cos(angle) * 4,   y + Math.sin(angle) * 4);
      ctx.lineTo(x + Math.cos(angle) * len, y + Math.sin(angle) * len);
      ctx.stroke();
    }

    ctx.globalAlpha = fade * 0.8;
    ctx.fillStyle   = "#ffffff";
    ctx.shadowBlur  = 14;
    ctx.beginPath();
    ctx.arc(x, y, 6 * (1 - t), 0, Math.PI * 2);
    ctx.fill();

    ctx.restore();
  }

  isDone(): boolean {
    return this.elapsed >= TOTAL;
  }
}
